/**
 * Every API failure is `{error_code, message, request_id}` (api_reference.md, Conventions). The
 * client branches on `code` and HTTP status, never on message text.
 */
export type ErrorKind =
  | 'entitlement' // 403 entitlement_required / plan_* → the locked-state component, not an error
  | 'legal' // 403 legal_acceptance_required → the blocking accept screen
  | 'unauthorised'
  | 'notFound'
  | 'gone' // 410, e.g. trade_detail_aged_out
  | 'conflict'
  | 'validation'
  | 'upgradeRequired' // 426 → reload prompt
  | 'rateLimited'
  | 'network'
  | 'server';

export class ApiError extends Error {
  readonly kind: ErrorKind;
  readonly status: number;
  readonly code: string | undefined;
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;
  readonly detail: unknown;

  constructor(init: {
    kind: ErrorKind;
    status: number;
    message: string;
    code?: string | undefined;
    requestId?: string | undefined;
    retryAfterSeconds?: number | undefined;
    detail?: unknown;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.kind = init.kind;
    this.status = init.status;
    this.code = init.code;
    this.requestId = init.requestId;
    this.retryAfterSeconds = init.retryAfterSeconds;
    this.detail = init.detail;
  }
}

export const isApiError = (e: unknown): e is ApiError => e instanceof ApiError;

const fallbackMessage: Record<ErrorKind, string> = {
  entitlement: 'This is not included in your plan.',
  legal: 'Please review and accept the updated terms to continue.',
  unauthorised: 'Please sign in again.',
  notFound: 'This could not be found. It may have been deleted.',
  gone: 'This is no longer available.',
  conflict: 'That conflicts with something that already exists.',
  validation: 'Some of that was not valid.',
  upgradeRequired: 'A newer version of SixOne8 is available.',
  rateLimited: 'That was a lot of requests at once. Try again shortly.',
  network: 'Could not reach SixOne8. Check your connection and try again.',
  server: 'Something went wrong on our side. Try again in a moment.',
};

export function kindFor(status: number, code: string | undefined): ErrorKind {
  if (code === 'legal_acceptance_required') return 'legal';
  if (code === 'entitlement_required' || code?.startsWith('plan_')) return 'entitlement';
  switch (status) {
    case 401:
      return 'unauthorised';
    case 402:
      return 'entitlement';
    case 403:
      return 'unauthorised';
    case 404:
      return 'notFound';
    case 409:
      return 'conflict';
    case 410:
      return 'gone';
    case 400:
    case 422:
      return 'validation';
    case 426:
      return 'upgradeRequired';
    case 429:
      return 'rateLimited';
    default:
      return 'server';
  }
}

/** Builds an ApiError from a failed Response whose body has already been parsed (or not). */
export function apiErrorFrom(response: Response, body: unknown): ApiError {
  const envelope = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>;
  const code = typeof envelope.error_code === 'string' ? envelope.error_code : undefined;
  const kind = kindFor(response.status, code);
  let message = typeof envelope.message === 'string' ? envelope.message : undefined;
  // FastAPI validation errors arrive as {detail: [...]}; keep them for field-level display.
  if (!message && typeof envelope.detail === 'string') message = envelope.detail;
  const retryAfter = Number(response.headers.get('retry-after'));
  return new ApiError({
    kind,
    status: response.status,
    code,
    message: message ?? fallbackMessage[kind],
    requestId: typeof envelope.request_id === 'string' ? envelope.request_id : undefined,
    retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    detail: envelope.detail,
  });
}

export const networkError = (cause: unknown) =>
  new ApiError({
    kind: 'network',
    status: 0,
    message: fallbackMessage.network,
    detail: cause,
  });

/** A message that is safe to show a user for any thrown value. */
export function messageOf(e: unknown): string {
  if (isApiError(e)) return e.message;
  return fallbackMessage.server;
}
